import store from '../../../store';
import setGlobalState from '../../../redux/setGlobalState';
import type {AiAgentFormId} from '../../../redux/aiAgent/aiAgent.type';
import {getEventTypeIntent, getFormIdForIntent} from './agentSchema';
import {isPlainObject} from './agentObjectUtils';

function hasValue(value: unknown): boolean {
    if(value == null) {
        return false;
    }
    if(typeof value === 'string') {
        return value.trim().length > 0;
    }
    if(typeof value === 'number') {
        return Number.isFinite(value);
    }
    if(Array.isArray(value)) {
        return value.length > 0;
    }
    return true;
}

function setIfPresent(out: Record<string, unknown>, key: string, value: unknown): void {
    if(hasValue(value)) {
        out[key] = typeof value === 'string' ? value.trim() : value;
    }
}

/** Copy `src[field]` → `out[prefix.field]` (or `out[field]` when prefix is empty). */
function pick(
    out: Record<string, unknown>,
    src: Record<string, unknown> | null | undefined,
    fields: string[],
    prefix = '',
): void {
    if(!src) {
        return;
    }
    fields.forEach(field => {
        setIfPresent(out, prefix ? `${prefix}.${field}` : field, src[field]);
    });
}

function snapshotLocation(out: Record<string, unknown>, values: Record<string, unknown>): void {
    const location = isPlainObject(values.location) ? values.location : null;
    const mapTitle = isPlainObject(location?.locationMap) ? location.locationMap.title : undefined;
    const raw = (typeof location?.location === 'string' && location.location.trim())
        || (typeof mapTitle === 'string' ? mapTitle.trim() : '');
    if(raw) {
        out['location.location'] = raw;
    }
}

function snapshotNewEventFormValues(
    values: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
    if(!values || !isPlainObject(values)) {
        return {};
    }
    const out: Record<string, unknown> = {};
    pick(out, values, ['title', 'description', 'fullDescription', 'budgetIndex', 'logo', 'category', 'artists']);
    pick(out, isPlainObject(values.date) ? values.date : null, ['startDate', 'startTime', 'endDate', 'endTime', 'duration', 'timeOpen'], 'date');
    snapshotLocation(out, values);
    pick(out, isPlainObject(values.map) ? values.map : null, ['title', 'eid'], 'map');
    pick(out, isPlainObject(values.locale) ? values.locale : null, ['lang', 'timeZone'], 'locale');
    return out;
}

function snapshotTicketFormValues(
    values: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
    if(!values || !isPlainObject(values)) {
        return {};
    }
    const out: Record<string, unknown> = {};
    setIfPresent(out, 'title', values.title ?? values.subscriptionName);
    setIfPresent(out, 'prices', hasValue(values.prices) ? values.prices : values.price);
    setIfPresent(out, 'limit', values.limit ?? values.ticketsLimit);
    setIfPresent(out, 'amount', values.amount ?? values.ticketsAmount);
    pick(out, values, [
        'credits', 'text', 'active', 'extras',
        'startDate', 'startTime', 'endDate', 'endTime',
        'startDateActive', 'endDateActive', 'displayBeforeSale', 'displayAfterSale',
    ]);

    if(isPlainObject(values.description)) {
        pick(out, values.description, ['summary', 'description'], 'description');
    } else if(typeof values.description === 'string' && values.description.trim()) {
        out['description.description'] = values.description.trim();
    }

    pick(out, isPlainObject(values.payments) ? values.payments : null, [
        'paymentsMethod', 'numberPayments', 'hasBillings', 'extraException',
    ], 'payments');
    return out;
}

const FORM_MAPPERS: Partial<
    Record<AiAgentFormId, (values: Record<string, unknown> | null | undefined) => Record<string, unknown>>
> = {
    NEW_EVENT: snapshotNewEventFormValues,
    CREATE_TICKET: snapshotTicketFormValues,
};

export function snapshotAgentFormValues(
    formId: AiAgentFormId,
    values: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
    return FORM_MAPPERS[formId]?.(values) ?? {};
}

/** Also pull already-saved event fields from Redux (after step persist). */
function snapshotEventStateForAgent(): Record<string, unknown> {
    const event = store.getState().event;
    if(!event) {
        return {};
    }
    const out: Record<string, unknown> = {};
    // Do NOT copy event.timeStart — stale defaults must not skip the date ask
    pick(out, event as Record<string, unknown>, ['title', 'description', 'category', 'artists']);
    const location = (typeof event.location === 'string' && event.location.trim())
        || (typeof event.locationMap?.title === 'string' ? event.locationMap.title.trim() : '');
    if(location) {
        out['location.location'] = location;
    }
    return out;
}

export function mergeCurrentFormValues(fields: Record<string, unknown> | null | undefined): void {
    if(!fields || !Object.keys(fields).length) {
        return;
    }
    const prev = store.getState().aiAgent?.currentFormValues || {};
    setGlobalState('aiAgent.currentFormValues', {...prev, ...fields});
}

/**
 * Replace currentFormValues with the live form snapshot (cleared fields drop out).
 * Pending fills that are not in Formik yet are kept until Formik catches up.
 */
export function syncCurrentFormValuesFromFormik(
    formId: AiAgentFormId,
    values: Record<string, unknown>,
): void {
    const fromForm = snapshotAgentFormValues(formId, values);
    const eventTypeIntent = getEventTypeIntent();
    const eventFormId = eventTypeIntent ? getFormIdForIntent(eventTypeIntent) : null;
    const fromEvent = formId === eventFormId ? snapshotEventStateForAgent() : {};
    const live = {...fromEvent, ...fromForm};

    const pending = store.getState().aiAgent?.pendingFormFill;
    const next = {...live};
    if(pending?.formId === formId && pending.fields) {
        Object.entries(pending.fields).forEach(([key, value]) => {
            if(!(key in live) && hasValue(value)) {
                next[key] = value;
            }
        });
    }

    setGlobalState('aiAgent.currentFormValues', next);
}
